import { useEffect, useState, useContext } from 'react';
import { Box, Grid, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, getYear } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useCrud } from '../../../lib/hooks';
import {
    AppContext,
    SttButtonCancel,
    SttButtonDelete,
    SttButtonRecover,
    SttFormInterval,
    SttButtonSave,
    SttFacilityList,
    SttFormDate,
    SttFormSelect,
    SttFormText,
    SttFormTime,
    SttSelectFixedInterval,
} from '../../../sporttia/all';
import { getPath } from '../../Pages';
import type { Facility } from '../../../types/models/Facility';
import { PERMISSIONS } from '../../../lib/permissions';

type Restriction = {
    id?: string;
    name?: string;
    type?: string;
    dateini?: string;
    dateend?: string;
    timeini?: string;
    timeend?: string;
    isDefault?: string;
    windowIniInterval?: string;
    windowEndInterval?: string;
    windowIniTime?: string;
    windowEndTime?: string;
    trash?: number;
    created?: string;
    facilities?: Facility[];
    groups?: unknown;
    numBookings?: string;
    interval?: string;
};

type Props = {
    id?: string;
    trash?: unknown;
    restrictionInit?: Restriction;
    weekdaysInit?: unknown;
    monthsInit?: unknown;
};

export default function RestrictionDetails({
    id,
    restrictionInit,
}: Props): React.ReactElement | false {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [restriction, setRestriction, setProperty, , Post, Put, Delete] =
        useCrud<Restriction>(restrictionInit);
    const [showNumBookings, setShowNumBookings] = useState<boolean>(false);
    const [showPeriod, setShowPeriod] = useState<boolean>(false);
    const [showWindow, setShowWindow] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (id === 'create') {
            const currentYear = getYear(new Date());
            setRestriction({
                name: '',
                type: 'NUM_BOOKINGS',
                dateini: `${currentYear}-01-01`,
                dateend: `${currentYear}-12-31`,
                timeini: '00:00',
                timeend: '23:59',
                isDefault: '0',
                windowIniInterval: '0000-00-00 00:00:00',
                windowEndInterval: '0000-00-00 00:00:00',
            });
        }
    }, [id, setRestriction]);

    /**
     * Restriction type changes
     */
    useEffect(() => {
        if (restriction) {
            setShowNumBookings(
                ['NUM_BOOKINGS', 'NUM_ACTIVE_BOOKINGS'].includes(
                    restriction.type || '',
                ),
            );
            setShowPeriod(restriction.type === 'NUM_BOOKINGS');
            setShowWindow(restriction.type === 'WINDOW');
        }
    }, [restriction]);

    /**
     * Create or update restriction
     */
    function saveRestriction() {
        setLoading(true);
        // Updating
        if (restriction?.id) {
            const {
                trash: _trash,
                created: _created,
                id: _idRestriction,
                facilities: _facilities,
                groups: _groups,
                ...putRestrictionRequestBody
            } = restriction;

            Put(`/bookings/restrictions/${restriction.id}`, {
                ...putRestrictionRequestBody,
            }).finally(() => setLoading(false));

            // Creating
        } else {
            Post(`/scs/${cxt?.sc?.id}/bookings/restrictions`, {
                ...restriction,
            })
                .then((response: unknown) => {
                    const data = response as {
                        bookingRestriction?: Restriction;
                    };
                    if (data.bookingRestriction) {
                        setRestriction(data.bookingRestriction);
                        navigate(
                            getPath(`restriction`, {
                                id: data.bookingRestriction.id,
                            }),
                            { replace: true },
                        );
                    }
                })
                .finally(() => setLoading(false));
        }
    }

    /**
     * Delete restriction
     */
    function deleteRestriction() {
        Delete(`/bookings/restrictions/${restriction?.id}`).then(() => {
            navigate(getPath('restrictions', { replace: true }));
        });
    }

    /**
     * Recover restriction
     */
    function recoverRestriction() {
        Put(
            `/bookings/restrictions/${restriction?.id}/recover`,
            undefined,
            true,
        ).then(() => {
            setRestriction({
                ...restriction,
                trash: 0,
            });
        });
    }

    /**
     * Add facility to restrcition
     */
    function addFacility(facility: Facility) {
        Post(`/bookings/restrictions/${id}/facilities/${facility.id}`).then(
            () => {
                setProperty({
                    name: 'facilities',
                    value: (restriction?.facilities || []).concat([
                        facility,
                    ]) as any,
                });
            },
        );
    }

    /**
     * Delete facility from restriction
     */
    function removeFacility(facility: Facility) {
        Delete(`/bookings/restrictions/${id}/facilities/${facility.id}`).then(
            () => {
                setProperty({
                    name: 'facilities',
                    value: (restriction?.facilities || []).filter(
                        (fct) => fct.id !== facility.id,
                    ) as any,
                });
            },
        );
    }

    if (!restriction) {
        return false;
    }

    // Wrapper to convert form onChange signature to setProperty signature
    const handleFormChange = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }) => {
        if (name) {
            setProperty({
                name: name as keyof Restriction,
                value: value as any,
            });
        }
    };

    return (
        <>
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 7 }}>
                    <Grid container spacing={3}>
                        <SttFormText
                            grid
                            md={12}
                            sm={12}
                            xs={12}
                            caption={t('NameAndDescription')}
                            name="name"
                            defVal={restriction.name}
                            onChange={handleFormChange}
                        />

                        <SttFormSelect
                            grid
                            xs={12}
                            xl={4}
                            caption={t('Type')}
                            name="type"
                            defVal={restriction.type}
                            options={cxt?.constants?.restrictionTypes?.map(
                                (restr: { label: string; id: string }) => ({
                                    caption: restr.label,
                                    value: restr.id,
                                }),
                            )}
                            onChange={handleFormChange}
                        />

                        <SttFormDate
                            grid
                            xs={12}
                            md={6}
                            xl={3}
                            caption={t('ValidFrom')}
                            name="dateini"
                            defVal={restriction.dateini}
                            onChange={({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (name) {
                                    setProperty({
                                        name: name as keyof Restriction,
                                        value: format(
                                            parseISO(value as string),
                                            'yyyy-MM-dd',
                                        ) as any,
                                    });
                                }
                            }}
                        />

                        <SttFormDate
                            grid
                            xs={12}
                            md={6}
                            xl={3}
                            caption={t('until')}
                            name="dateend"
                            defVal={restriction.dateend}
                            onChange={({
                                name,
                                value,
                            }: {
                                name: string | undefined;
                                value: unknown;
                            }) => {
                                if (name) {
                                    setProperty({
                                        name: name as keyof Restriction,
                                        value: format(
                                            parseISO(value as string),
                                            'yyyy-MM-dd',
                                        ) as any,
                                    });
                                }
                            }}
                        />
                        <SttFormTime
                            grid
                            xs={12}
                            md={6}
                            xl={3}
                            caption={t('TimeIni')}
                            name="timeini"
                            min="00:00"
                            max="23:59"
                            defVal={restriction.timeini}
                            interval={30}
                            onChange={({ name, value }) => {
                                setProperty({
                                    name: name as keyof Restriction,
                                    value: value as any,
                                });
                            }}
                            timeRangeOptions={{ includeMaxTime: true }}
                        />
                        <SttFormTime
                            grid
                            xs={12}
                            md={6}
                            xl={3}
                            caption={t('TimeEnd')}
                            name="timeend"
                            min="00:00"
                            max="23:59"
                            defVal={restriction.timeend}
                            interval={30}
                            onChange={({ name, value }) => {
                                setProperty({
                                    name: name as keyof Restriction,
                                    value: value as any,
                                });
                            }}
                            timeRangeOptions={{ includeMaxTime: true }}
                        />

                        <SttFormSelect
                            grid
                            md={12}
                            sm={12}
                            xs={12}
                            caption={t('Default')}
                            name="isDefault"
                            defVal={+(restriction.isDefault || '0')}
                            options={[
                                {
                                    caption: t(
                                        'page.sc.restriction.default.no',
                                    ),
                                    value: '0',
                                },
                                {
                                    caption: t(
                                        'page.sc.restriction.default.yes',
                                    ),
                                    value: '1',
                                },
                            ]}
                            onChange={handleFormChange}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Box my={4}>
                            <Divider />
                        </Box>
                    </Grid>

                    <Grid container spacing={3}>
                        {showNumBookings && (
                            <SttFormText
                                grid
                                md={5}
                                xs={12}
                                xl={4}
                                type="number"
                                inputProps={{ min: 1, max: 50, step: 1 }}
                                caption={t('NumberOfBookings')}
                                name="numBookings"
                                defVal={restriction.numBookings}
                                onChange={handleFormChange}
                            />
                        )}

                        {showPeriod && (
                            <Grid size={{ md: 7, xs: 12, xl: 4 }}>
                                <SttSelectFixedInterval
                                    cleanable
                                    caption={t('Period')}
                                    name="interval"
                                    options={['!NO PERIOD']}
                                    defVal={restriction.interval || ''}
                                    onChange={({ name, value }) => {
                                        setProperty({
                                            name: name as keyof Restriction,
                                            value: value as any,
                                        });
                                    }}
                                />
                            </Grid>
                        )}

                        {showWindow && (
                            <>
                                <SttFormInterval
                                    grid
                                    md={8}
                                    xs={12}
                                    xl={4}
                                    caption={t('Begin')}
                                    name="windowIniInterval"
                                    defVal={restriction.windowIniInterval}
                                    onChange={({ name, value }) => {
                                        setProperty({
                                            name: name as keyof Restriction,
                                            value: value as any,
                                        });
                                    }}
                                />

                                <SttFormTime
                                    grid
                                    md={4}
                                    xs={12}
                                    xl={3}
                                    caption={t('TimeIni')}
                                    name="windowIniTime"
                                    min="00:00"
                                    defVal={restriction.windowIniTime}
                                    interval={30}
                                    onChange={({ name, value }) => {
                                        setProperty({
                                            name: name as keyof Restriction,
                                            value: value as any,
                                        });
                                    }}
                                />

                                <SttFormInterval
                                    grid
                                    md={8}
                                    xs={12}
                                    xl={4}
                                    caption={t('End')}
                                    name="windowEndInterval"
                                    defVal={restriction.windowEndInterval}
                                    onChange={({ name, value }) => {
                                        setProperty({
                                            name: name as keyof Restriction,
                                            value: value as any,
                                        });
                                    }}
                                />

                                <SttFormTime
                                    grid
                                    md={4}
                                    xs={12}
                                    xl={3}
                                    caption={t('TimeEnd')}
                                    name="windowEndTime"
                                    min="00:00"
                                    defVal={restriction.windowEndTime}
                                    interval={30}
                                    onChange={({ name, value }) => {
                                        setProperty({
                                            name: name as keyof Restriction,
                                            value: value as any,
                                        });
                                    }}
                                />
                            </>
                        )}
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                    {restriction?.facilities !== undefined &&
                        restriction?.facilities !== null && (
                            <SttFacilityList
                                permission={PERMISSIONS.RESTRICTION.MODIFY}
                                onAdd={addFacility as any}
                                onDelete={removeFacility as any}
                                facilities={restriction.facilities as any}
                            />
                        )}
                </Grid>
            </Grid>

            <Box className="mt-6 flex justify-start gap-4 flex-wrap">
                {restriction.id && !restriction.trash && (
                    <SttButtonDelete
                        onClick={deleteRestriction}
                        permission={PERMISSIONS.RESTRICTION.DELETE}
                    />
                )}

                {!restriction.trash && (
                    <SttButtonSave
                        loading={loading}
                        onClick={saveRestriction}
                        permission={
                            restriction.id
                                ? PERMISSIONS.RESTRICTION.MODIFY
                                : PERMISSIONS.RESTRICTION.CREATE
                        }
                    />
                )}

                {!restriction.id && (
                    <SttButtonCancel onClick={() => navigate(-1)} />
                )}

                {restriction.trash && (
                    <SttButtonRecover
                        onClick={recoverRestriction}
                        permission={PERMISSIONS.RESTRICTION.DELETE}
                    />
                )}
            </Box>
        </>
    );
}
